if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      if (this.zoomInitHandler) {
        window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
      }
    }

    init() {
      this.section = this.closest('.js-product');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');

      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      if (this.zoomLinks) {
        this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
        this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
        window.addEventListener('on:debounced-resize', this.zoomInitHandler);
        this.initZoom();
        this.zoomLinks.forEach((el) => {
          el.addEventListener('click', (evt) => {
            evt.preventDefault();
          });
        });
      }

      if (this.section) {
        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
      }
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      // Update visible items when variant changes
      setTimeout(() => {
        this.setVisibleItems();
      }, 100);

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(
          `[data-media-id="${evt.detail.variant.featured_media.id}"]`
        );
        if (variantMedia && variantMedia.offsetWidth > 0 && variantMedia.style.display !== 'none') {
          this.customSetActiveMedia(variantMedia, true);
        }
      }

      // After media grouping and layout settle, activate the thumbnail that matches
      // the selected variant's featured media so that any thumbnail-click-specific
      // behavior runs (e.g. videos, zoom). Only fall back to the first visible
      // thumbnail when the variant has no featured media, otherwise clicking the
      // first thumb would snap the gallery back to the first image.
      setTimeout(() => {
        const thumbsContainer = this.querySelector('.media-thumbs');
        if (!thumbsContainer) return;

        const isThumbVisible = (thumb) => {
          const style = window.getComputedStyle(thumb);
          return style.display !== 'none' && style.visibility !== 'hidden';
        };

        const thumbItems = Array.from(thumbsContainer.querySelectorAll('.media-thumbs__item'));

        // Prefer the thumbnail for the current variant's featured media.
        let targetThumb = null;
        const featuredMediaId = evt.detail.variant && evt.detail.variant.featured_media
          ? String(evt.detail.variant.featured_media.id)
          : null;

        if (featuredMediaId) {
          targetThumb = thumbItems.find(
            (thumb) => thumb.dataset.mediaId === featuredMediaId && isThumbVisible(thumb)
          );
        }

        // Fall back to the first visible thumbnail (e.g. variant has no image).
        if (!targetThumb) {
          // Scroll thumbs back to the top so the new group's images start from the top
          if (thumbsContainer.scrollTo) {
            thumbsContainer.scrollTo({ top: 0, behavior: 'instant' });
          } else {
            thumbsContainer.scrollTop = 0;
          }
          targetThumb = thumbItems.find(isThumbVisible);
        }

        if (!targetThumb) return;

        const btn = targetThumb.querySelector('.media-thumbs__btn');
        if (btn && typeof btn.click === 'function') {
          btn.click();
        }
      }, 100);
    }

    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }
        return selector.querySelector('input:checked')?.value;
      }
      return null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {}
        };

        // set up grouping
        const variantMediaData = this.getMediaGroupData();
        let currentMediaOptionName = false;
        this.viewerItems = this.querySelectorAll('.media-viewer__item');
        this.viewerItems.forEach((item) => {
          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
              if (currentMediaOptionName !== variantMediaData.variantMedia[i].option) {
                currentMediaOptionName = variantMediaData.variantMedia[i].option;
              }
            }
          }
          if (currentMediaOptionName) {
            if (!this.mediaGroupMap.groups[currentMediaOptionName]) {
              this.mediaGroupMap.groups[currentMediaOptionName] = {
                name: currentMediaOptionName,
                items: []
              };
            }
            const groupItem = { main: item };
            if (this.thumbs) {
              groupItem.thumb = this.thumbs.querySelector(
                `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
              );
            }
            this.mediaGroupMap.groups[currentMediaOptionName].items.push(groupItem);
          }
        });

        // add helper
        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);
          for (let i = 0; i < groups; i += 1) {
            const group = groups[i];
            for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
              if (this.mediaGroupMap.groups[group].items[j] === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }
          return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
      const mediaGroupMap = this.getMediaGroupMap();
      const selectedGroup = groupName
        ? mediaGroupMap.groups[groupName]
        : mediaGroupMap.groupFromItem(this.currentItem);

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;
          this.viewerItems.forEach((item) => {
            item.style.display = 'none';
            item.classList.remove('media-viewer__item--single');
          });
          this.thumbsItems.forEach((item) => {
            item.style.display = 'none';
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';
            if (item.thumb) {
              item.thumb.style.display = '';
            }
            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });
          this.setVisibleItems();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add('media-viewer__item--single');
          }
          
          // Update dots to match visible items
          this.setVisibleItems();
        }
      } else {
        this.viewerItems.forEach((item) => {
          item.style.display = '';
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = '';
        });
        // Update dots when showing all items
        this.setVisibleItems();
      }
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      
      // Initialize dots for visible items
      if (this.visibleItems && this.visibleItems.length > 0) {
        this.updateDotsForVisibleItems(this.visibleItems);
      }
      
      if (this.visibleItems.length <= 1) return;

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      if (!this.isFeatured && document.hasFocus()) {
        // Eager load the slider images for smooth UX
        this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, 500 * (index + 1));
        });
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      if (currentItem && currentItem.offsetWidth > 0 && currentItem.style.display !== 'none') {
        this.customSetActiveMedia(currentItem, true);
      } else if (this.visibleItems.length > 0) {
        this.customSetActiveMedia(this.visibleItems[0], true);
      }
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));

      if (this.controls) {
        this.controls.addEventListener('click', this.handleNavClick.bind(this));
      }

      if (this.thumbs) {
        // Delegate click from the thumbs container (existing behavior)
        this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));

        // Also bind directly to each thumbnail button in case something stops propagation
        const thumbButtons = this.thumbs.querySelectorAll('.media-thumbs__btn');
        thumbButtons.forEach((btn) => {
          btn.addEventListener('click', (evt) => {
            this.handleThumbClick(evt);
          });
        });
      }
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    
    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomWidth = Number(el.querySelector('.zoom-image').dataset.originalWidth || 0);
        const imageWidth = el.querySelector('.product-image').getBoundingClientRect().width;
        if (theme.mediaMatches.md && ((zoomWidth - 75) > (imageWidth))) {
          el.addEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
        } else {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.classList.add('pointer-events-none');
        }
      });
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // Handle circular scrolling
        if (newIndex >= this.visibleItems.length) {
          this.viewer.scrollLeft = 0;  // Scroll back to the start
          this.customSetActiveMedia(this.visibleItems[0], false);
          return;
        } if (newIndex < 0) {
          this.viewer.scrollLeft = this.viewer.scrollWidth;  // Scroll to the end
          this.customSetActiveMedia(this.visibleItems[this.visibleItems.length - 1], false);
          return;
        }

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
      
      // Update dots on scroll
      this.updateDotsActiveState();
    }


    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (!evt.target.matches('.media-ctrl__btn')) return;
    
      const isNext = evt.target === this.nextBtn;
      let itemToShow;
    
      if (isNext) {
        itemToShow = this.visibleItems[this.currentIndex + 1] || this.visibleItems[0]; // Loop to the start
      } else {
        itemToShow = this.visibleItems[this.currentIndex - 1] || this.visibleItems[this.visibleItems.length - 1]; // Loop to the end
      }
    
      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }
    

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      // Ensure we target a thumbnail list item, not an inner element like the image or SVG
      const thumb = evt.target.closest('.media-thumbs__item[data-media-id]');
      if (!thumb) {
        return;
      }

      const mediaId = thumb.dataset.mediaId;
      if (!mediaId) {
        return;
      }

      // Always find the corresponding main viewer item, not the thumbnail itself
      const itemToShow = this.viewer.querySelector(
        `.media-viewer__item[data-media-id="${mediaId}"]`
      );
      if (!itemToShow) {
        return;
      }

      // Scroll to and activate the matching main image
      this.customSetActiveMedia(itemToShow, true);
      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (mediaItem === this.currentItem) return;
      window.pauseAllMedia(this);
    
      // Ensure visibleItems is set and up to date
      if (!this.visibleItems || this.visibleItems.length === 0) {
        this.setVisibleItems();
      }
    
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);
      
      // If item not in visible items, recalculate visible items
      if (this.currentIndex < 0) {
        this.setVisibleItems();
        this.currentIndex = this.visibleItems.indexOf(mediaItem);
      }
    
      if (scrollToItem && this.currentItem) {
        this.viewer.scrollTo({ left: this.currentItem?.offsetLeft });
      }
    
      if (this.thumbs) this.setActiveThumb();
    
      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = false; // Enable looping
        }
    
        if (this.nextBtn) {
          this.nextBtn.disabled = false; // Enable looping
        }
    
        if (this.counterCurrent) {
          this.counterCurrent.textContent = (this.currentIndex >= 0 ? this.currentIndex : 0) + 1;
        }
      }
    
      // Update dots active state on mobile
      this.updateDotsActiveState();
    
      this.announceLiveRegion(this.currentItem, (this.currentIndex >= 0 ? this.currentIndex : 0) + 1);
    
      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }
    

    /**
     * Updates the active state of dots/progress bar on mobile.
     */
    updateDotsActiveState() {
      const dotsContainer = this.querySelector('.dots-btn');
      if (!dotsContainer) return;
      
      // Get visible items (not hidden by variant filtering)
      const visibleItems = this.visibleItems || this.viewerItems.filter(item => {
        return item.offsetWidth > 0 && 
               item.style.display !== 'none' &&
               !item.classList.contains('media-viewer__item--variant');
      });
      
      // Update dots to match visible items only
      this.updateDotsForVisibleItems(visibleItems);
      
      // Update active state
      const visibleIndex = visibleItems.indexOf(this.currentItem);
      if (visibleIndex >= 0) {
        const dots = dotsContainer.querySelectorAll('.dotbtn');
        dots.forEach((dot, index) => {
          if (index === visibleIndex) {
            dot.classList.add('active');
          } else {
            dot.classList.remove('active');
          }
        });
      }
    }

    /**
     * Updates dots to match only visible items for current variant.
     */
    updateDotsForVisibleItems(visibleItems) {
      const dotsContainer = this.querySelector('.dots-btn');
      if (!dotsContainer) return;
      
      const visibleCount = visibleItems.length;
      const existingDots = dotsContainer.querySelectorAll('.dotbtn');
      const existingCount = existingDots.length;
      
      // Check if dots need to be rebuilt (different count or media IDs don't match)
      let needsRebuild = visibleCount !== existingCount;
      if (!needsRebuild && existingCount > 0) {
        // Verify that existing dots match visible items by media ID
        visibleItems.forEach((item, index) => {
          const dot = existingDots[index];
          if (!dot || !item.dataset.mediaId || 
              parseInt(dot.dataset.mediaId || '0') !== parseInt(item.dataset.mediaId)) {
            needsRebuild = true;
          }
        });
      }
      
      // If count and items match, just update the mapping
      if (!needsRebuild && visibleCount > 0) {
        visibleItems.forEach((item, index) => {
          const dot = existingDots[index];
          if (dot) {
            dot.dataset.visibleIndex = index;
            dot.dataset.mediaId = item.dataset.mediaId;
            dot.style.display = '';
          }
        });
        // Update active state will be handled by updateDotsActiveState
        return;
      }
      
      // Rebuild dots for visible items only
      dotsContainer.innerHTML = '';
      
      visibleItems.forEach((item, index) => {
        const dotBtn = document.createElement('div');
        const isActive = item === this.currentItem || (index === 0 && !this.currentItem);
        dotBtn.className = `dotbtn ${isActive ? 'active' : ''}`;
        dotBtn.setAttribute('data-index', index);
        dotBtn.setAttribute('data-visible-index', index);
        dotBtn.setAttribute('data-media-id', item.dataset.mediaId || '');
        dotBtn.setAttribute('aria-controls', 'gallery-viewer');
        dotBtn.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="69" height="6" viewBox="0 0 69 6" fill="none">
            <rect width="6" height="6" fill="black"/>
          </svg>
        `;
        dotBtn.addEventListener('click', () => {
          this.scrollToVisibleItem(index);
        });
        dotsContainer.appendChild(dotBtn);
      });
      
      // Show/hide dots based on visible items count (mobile only)
      if (window.innerWidth <= 768) {
        if (visibleCount <= 1) {
          dotsContainer.style.display = 'none';
        } else {
          dotsContainer.style.display = 'flex';
        }
      }
    }

    /**
     * Scrolls to a specific visible item by index.
     */
    scrollToVisibleItem(visibleIndex) {
      const visibleItems = this.visibleItems || this.viewerItems.filter(item => {
        return item.offsetWidth > 0 && 
               item.style.display !== 'none' &&
               !item.classList.contains('media-viewer__item--variant');
      });
      
      if (visibleItems[visibleIndex]) {
        const item = visibleItems[visibleIndex];
        this.viewer.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
        this.customSetActiveMedia(item, false);
      }
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem?.dataset.mediaId}"]`
      );
      const btn = this.currentThumb?.querySelector('button');
    
      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });
    
      btn?.classList.add('is-active');
      btn?.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }
    

    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => {
        // Check if element is actually visible (not hidden by CSS or inline styles)
        const computedStyle = window.getComputedStyle(el);
        const isVisible = computedStyle.display !== 'none' && 
                         computedStyle.visibility !== 'hidden' &&
                         el.offsetWidth > 0 && 
                         el.offsetHeight > 0 &&
                         el.clientWidth > 0;
        
        // Also exclude variant items that are hidden (via CSS :not(:first-child) rule)
        // But include variant items if they're actually visible
        return isVisible;
      });
      
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
      
      // Update dots when visible items change (e.g., on variant change)
      this.updateDotsForVisibleItems(this.visibleItems);
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
      }
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);
}

// Global function for scrolling to item via dots (called from Liquid template)
window.scrollToItem = function(index) {
  const mediaGallery = document.querySelector('media-gallery');
  if (!mediaGallery) return;
  
  // Use the scrollToVisibleItem method if available
  if (mediaGallery.scrollToVisibleItem) {
    mediaGallery.scrollToVisibleItem(index);
    return;
  }
  
  // Fallback to original method
  const viewer = mediaGallery.querySelector('.media-viewer');
  if (!viewer) return;
  
  // Get visible items only
  const allItems = viewer.querySelectorAll('.media-viewer__item');
  const visibleItems = Array.from(allItems).filter(item => {
    return item.offsetWidth > 0 && 
           item.style.display !== 'none' &&
           !item.classList.contains('media-viewer__item--variant');
  });
  
  if (visibleItems[index]) {
    const item = visibleItems[index];
    const itemLeft = item.offsetLeft;
    viewer.scrollTo({ left: itemLeft, behavior: 'smooth' });
    
    // Update active media
    if (mediaGallery.customSetActiveMedia) {
      mediaGallery.customSetActiveMedia(item, false);
    }
  }
};

// Isolate thumbnail scroll/observer logic to avoid polluting global scope
(function initMediaThumbObserver() {
  const thumbsContainer = document.querySelector('.media-thumbs');
  if (!thumbsContainer || typeof IntersectionObserver === 'undefined') return;

  const thumbs = thumbsContainer.querySelectorAll('.media-thumbs__item');

  const observerOptions = {
    root: thumbsContainer,
    rootMargin: '0px',
    threshold: 0.5
  };

  let currentActiveId = null;
  let userHasScrolledThumbs = false;

  const observerCallback = (entries) => {
    if (!userHasScrolledThumbs) return; // Only after the user scrolls thumbsContainer

    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const thumbItem = entry.target;
        const mediaId = thumbItem.getAttribute('data-media-id');

        if (currentActiveId === mediaId) return;

        currentActiveId = mediaId;

        const button = thumbItem.querySelector('.media-thumbs__btn');
        if (button) {
          button.click();
        }
      }
    });
  };

  const mediaThumbsObserver = new IntersectionObserver(observerCallback, observerOptions);

  const onThumbsScroll = () => {
    if (userHasScrolledThumbs) return; // Already scrolled, no need to re-run

    userHasScrolledThumbs = true;

    // Start observing each thumb
    thumbs.forEach((thumb) => {
      mediaThumbsObserver.observe(thumb);
    });

    // Optional: Remove scroll listener after first scroll
    thumbsContainer.removeEventListener('scroll', onThumbsScroll);
  };

  thumbsContainer.addEventListener('scroll', onThumbsScroll);
}());

